import React, {
  useEffect, useMemo, useReducer, useState,
} from 'react';
import {
  Badge,
  Button,
  CheckPicker,
  IconButton,
  Input,
  InputGroup,
  Panel,
  Stack,
  Table,
} from 'rsuite';
import axios from 'axios';
import styled from '@emotion/styled';
import SearchIcon from '@rsuite/icons/Search';
import { Icon } from '@rsuite/icons';
import { FaFilter } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { useStoreState } from '../store/hooks';
import { getPartners } from '../api/partner/partner';
import {
  fetchAssignableRoles,
  fetchUsers,
  saveUserPartners,
  saveUserRoles,
} from '../components/UserSetup/usersService';
import {
  ROLE_OPTIONS,
  UsersFilter,
  hasPartnersChanged,
  hasRolesChanged,
  initialState,
  isLoading,
  reducer,
  selectFilterableRoleOptions,
  selectFilteredUsers,
  selectLockedRoles,
  selectPartnerCodes,
} from '../components/UserSetup/usersState';
import UsersFilterForm from '../components/UserSetup/UsersFilterForm';
import UserPasswordDialog from '../components/UserSetup/UserPasswordDialog';
import { ManagedUser } from '../api/userManagement/userManagementApiTypes';
import { Permission, UserRole } from '../store/user/types';
import { USER_SETUP_CREATE } from '../routes/links';
import handleError from '../api/apiErrorHandle';
import { showSuccessMessage } from '../utils/message';
import { getUsedFilterCount } from '../utils/filter';

const ROLES_UPDATED_MESSAGE = 'Roles updated! The user sees the change after signing in again.';

const ROLES_UPDATED_MESSAGE_DURATION = 4000;

const ClippedCell = styled(Table.Cell)`
  & .rs-table-cell-content {
    overflow-x: auto;
    overflow-y: hidden;
    text-overflow: clip;
    max-width: 250px;
  }
`;

const PickerContainer = styled.div`
  max-height: 50px;
  overflow-y: auto;
`;

export default function UsersTable(): JSX.Element {
  const navigate = useNavigate();
  const { currentUser, hasPermission } = useStoreState(state => state.user);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [filterValue, setFilterValue] = useState<string>('');
  const [appliedFilter, setAppliedFilter] = useState<string>('');
  const [advancedFilterVisible, setAdvancedFilterVisible] = useState(false);
  const [passwordDialogUserId, setPasswordDialogUserId] = useState<number>();

  const canCreateUser = hasPermission(Permission.UserSetupCreate);
  const canManageUsers = hasPermission(Permission.UserSetupManage);

  const filteredUsers = useMemo(
    () => selectFilteredUsers(state.users, appliedFilter),
    [state.users, appliedFilter],
  );

  const advancedFiltersCount = getUsedFilterCount(state.filter);

  function loadUsers(filter: UsersFilter, signal: AbortSignal): void {
    dispatch({ type: 'users/loading' });

    fetchUsers(filter, signal)
      .then(users => dispatch({ type: 'users/loaded', payload: users }))
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }

        handleError(error);
        dispatch({ type: 'users/failed' });
      });
  }

  useEffect(() => {
    const controller = new AbortController();
    loadUsers(state.filter, controller.signal);

    return () => {
      controller.abort();
    };
  }, [state.filter]);

  useEffect(() => {
    fetchAssignableRoles()
      .then(roles => dispatch({ type: 'roles/loaded', payload: roles }))
      .catch(error => handleError(error));
    getPartners()
      .then(partners => dispatch({ type: 'partners/loaded', payload: partners }))
      .catch(error => handleError(error));
  }, []);

  function search(): void {
    setAppliedFilter(filterValue);
  }

  function handleAdvancedFilterApplied(filter: UsersFilter): void {
    dispatch({ type: 'filter/applied', payload: filter });
  }

  function handleFilterKeyUp(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      search();
    }
  }

  function handleCreateUserClicked(): void {
    navigate(USER_SETUP_CREATE);
  }

  function handlePartnersSelected(user: ManagedUser, partnerCodes: string[]): void {
    dispatch({ type: 'user/partnersSelected', payload: { userId: user.id, partnerCodes } });
  }

  function handleAllPartnersToggled(user: ManagedUser, allSelected: boolean): void {
    handlePartnersSelected(user, allSelected ? [] : state.partners.map(partner => partner.code));
  }

  function handleRolesSelected(user: ManagedUser, roles: UserRole[]): void {
    dispatch({ type: 'user/rolesSelected', payload: { userId: user.id, roles } });
  }

  function handlePartnersClosed(user: ManagedUser): void {
    if (!hasPartnersChanged(state, user)) {
      return;
    }

    saveUserPartners(user)
      .then((updatedUser) => {
        dispatch({ type: 'user/partnersSaved', payload: updatedUser });
        showSuccessMessage('Partners updated!');
      })
      .catch((error) => {
        handleError(error);
        dispatch({ type: 'user/partnersReverted', payload: user.id });
      });
  }

  function handleRolesClosed(user: ManagedUser): void {
    if (!hasRolesChanged(state, user)) {
      return;
    }

    saveUserRoles(user)
      .then((updatedUser) => {
        dispatch({ type: 'user/rolesSaved', payload: updatedUser });
        showSuccessMessage(ROLES_UPDATED_MESSAGE, ROLES_UPDATED_MESSAGE_DURATION);
      })
      .catch((error) => {
        handleError(error);
        dispatch({ type: 'user/rolesReverted', payload: user.id });
      });
  }

  function handlePasswordDialogOpened(user: ManagedUser): void {
    setPasswordDialogUserId(user.id);
  }

  function handlePasswordDialogClosed(): void {
    setPasswordDialogUserId(undefined);
  }

  function renderPartnerNames(user: ManagedUser): string {
    return user.partners.map(partner => partner.partnerName ?? partner.partnerCode).join(', ');
  }

  function renderPartnersMenu(user: ManagedUser, menu: React.ReactNode): JSX.Element {
    const allSelected = user.partners.length === state.partners.length;

    return (
      <div>
        <label htmlFor="selectAllPartners" style={{ paddingLeft: '13px' }}>
          <input
            id="selectAllPartners"
            type="checkbox"
            checked={allSelected}
            style={{ marginRight: '7px' }}
            onChange={() => handleAllPartnersToggled(user, allSelected)}
          />
          Select All
        </label>
        {menu}
      </div>
    );
  }

  function renderPartnersCell(user: ManagedUser): JSX.Element {
    return (
      <PickerContainer>
        <CheckPicker
          data={state.partners}
          labelKey="name"
          valueKey="code"
          block
          cleanable={false}
          disabled={!canManageUsers}
          value={selectPartnerCodes(user)}
          renderValue={() => renderPartnerNames(user)}
          renderMenu={(menu) => renderPartnersMenu(user, menu)}
          onChange={(partnerCodes) => handlePartnersSelected(user, partnerCodes)}
          onClose={() => handlePartnersClosed(user)}
        />
      </PickerContainer>
    );
  }

  function renderRolesCell(user: ManagedUser): JSX.Element {
    return (
      <PickerContainer>
        <CheckPicker
          data={ROLE_OPTIONS}
          block
          cleanable={false}
          searchable={false}
          disabled={!user.rolesEditable}
          disabledItemValues={selectLockedRoles(state, user, currentUser)}
          value={user.roles}
          onChange={(roles) => handleRolesSelected(user, roles)}
          onClose={() => handleRolesClosed(user)}
        />
      </PickerContainer>
    );
  }

  function renderPasswordCell(user: ManagedUser): JSX.Element {
    return (
      <FontAwesomeIcon
        icon={faKey}
        onClick={() => handlePasswordDialogOpened(user)}
        style={{ width: 20, height: 20 }}
      />
    );
  }

  function renderAdvancedFilterButton(): JSX.Element {
    const filterButton = (
      <IconButton
        style={{ width: 150 }}
        appearance="primary"
        onClick={() => setAdvancedFilterVisible(!advancedFilterVisible)}
        placement="right"
        icon={<Icon as={FaFilter} />}
      >
        Advanced filter
      </IconButton>
    );

    if (advancedFiltersCount === 0) {
      return filterButton;
    }

    return (
      <Badge content={advancedFiltersCount} color="green">
        {filterButton}
      </Badge>
    );
  }

  return (
    <div>
      <UserPasswordDialog
        userId={passwordDialogUserId}
        onClose={() => handlePasswordDialogClosed()}
      />
      <Panel className="h100p">
        <Stack direction="row" alignItems="flex-start" spacing={10}>
          <InputGroup inside>
            <Input
              style={{ width: 400 }}
              placeholder="search by id, username, name"
              defaultValue={filterValue}
              onChange={setFilterValue}
              onKeyUp={(event) => handleFilterKeyUp(event)}
            />
            <InputGroup.Button onClick={() => search()}>
              <SearchIcon />
            </InputGroup.Button>
          </InputGroup>
          {canCreateUser && (
            <Button appearance="primary" onClick={() => handleCreateUserClicked()}>
              Create new user
            </Button>
          )}
          {renderAdvancedFilterButton()}
        </Stack>
        <UsersFilterForm
          visible={advancedFilterVisible}
          partners={state.partners}
          roleOptions={selectFilterableRoleOptions(currentUser)}
          onApply={filter => handleAdvancedFilterApplied(filter)}
        />
        <Table
          autoHeight
          bordered
          data={filteredUsers}
          loading={isLoading(state)}
          className="mt-20"
          rowHeight={60}
        >
          <Table.Column width={50} align="center" verticalAlign="middle">
            <Table.HeaderCell>Id</Table.HeaderCell>
            <Table.Cell dataKey="id" align="center" />
          </Table.Column>
          <Table.Column width={250} align="center" verticalAlign="middle">
            <Table.HeaderCell>Username</Table.HeaderCell>
            <ClippedCell dataKey="username" />
          </Table.Column>
          <Table.Column width={250} align="center" verticalAlign="middle">
            <Table.HeaderCell>Name</Table.HeaderCell>
            <ClippedCell dataKey="name" />
          </Table.Column>
          <Table.Column flexGrow={1} align="center" verticalAlign="middle">
            <Table.HeaderCell>Partners</Table.HeaderCell>
            <Table.Cell>
              {(rowData: ManagedUser) => renderPartnersCell(rowData)}
            </Table.Cell>
          </Table.Column>
          <Table.Column flexGrow={1} align="center" verticalAlign="middle">
            <Table.HeaderCell>Roles</Table.HeaderCell>
            <Table.Cell>
              {(rowData: ManagedUser) => renderRolesCell(rowData)}
            </Table.Cell>
          </Table.Column>
          {canManageUsers && (
            <Table.Column width={80} align="center" verticalAlign="middle">
              <Table.HeaderCell>Password</Table.HeaderCell>
              <Table.Cell>
                {(rowData: ManagedUser) => renderPasswordCell(rowData)}
              </Table.Cell>
            </Table.Column>
          )}
        </Table>
      </Panel>
    </div>
  );
}
